import React from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useSearchParams, useLocation } from "react-router-dom";
import ErrorBoundary from "./components/ErrorBoundary";
import { useRBAC } from "./hooks/useRBAC";
import { useRemediationWorkflows, useUsoc, useFeatureFlagsLoaded } from "./contexts/FeatureFlagsContext";
import Login from "./components/Login";
import BrandPreview from "./components/BrandPreview";
import TenantSelection from "./components/TenantSelection";
import Dashboard from "./components/Dashboard";
import Deals from './components/Deals';
import DealDashboard from './components/DealDashboard';
import BusinessUnits from './components/BusinessUnits';
import Remediation from './components/Remediation';
import AllRisks from './components/AllRisks';
import AttackPathsDetail from './components/AttackPathsDetail';
import RiskRegister from "./components/RiskRegister";
import UserManagement from "./components/UserManagement";
import SubscriptionManagement from "./components/SubscriptionManagement";
import IntegrationSettings from "./components/IntegrationSettings";
import WorkflowTemplatesPage from "./components/WorkflowTemplatesPage";
import RemediationScorecardPage from "./components/RemediationScorecardPage";
import RemediationScorecardFeatureGuard from "./components/RemediationScorecardFeatureGuard";
import ConsentCallback from "./components/ConsentCallback";
import PrivateRoute from "./components/PrivateRoute";
import RoleProtectedRoute from "./components/RoleProtectedRoute";
import AssessmentRisksView from "./components/AssessmentRisksView";
import EnhancedAssessment from "./components/EnhancedAssessment";
import BusinessUnitAssessment from "./components/BusinessUnitAssessment";
import BusinessUnitAssessmentRisksView from "./components/BusinessUnitAssessmentRisksView";
import BusinessUnitDashboard from "./components/BusinessUnitDashboard";

// Ransomware Imports
import DashboardRansomware from "./components/RansomwareDashboard";
import RansomwareOrganizations from "./components/RansomwareOrganizations";
import RansomwareAssessment from "./components/RansomwareAssessment";
import RansomwareDeals from "./components/RansomwareDeals";
import RansomwareBusinessUnits from "./components/RansomwareBusinessUnits";
import RansomwareAssessmentRisksView from "./components/RansomwareAssessmentRisksView";
import RansomwareExecutiveView from "./components/RansomwareExecutiveView";

// Dark Web Imports
import DarkWebDashboard from "./components/DarkWebDashboard";
import DarkWebOrganizations from "./components/DarkWebOrganizations";
import DarkWebAssessment from "./components/DarkWebAssessment";
import DarkWebDeals from "./components/DarkWebDeals";
import DarkWebBusinessUnits from "./components/DarkWebBusinessUnits";
import DarkWebAssessmentRisksView from "./components/DarkWebAssessmentRisksView";
import DarkWebDomainConfig from "./components/DarkWebDomainConfig";
import DarkWebLiveMonitoringDashboard from "./components/DarkWebLiveMonitoringDashboard";
import SecretsScanningDashboard from "./components/SecretsScanningDashboard";
import EsmDashboard from "./components/EsmDashboard";
import FortinetConfig from "./components/FortinetConfig";
import ConnectorsConfig from "./components/ConnectorsConfig";
import SuperConfig from "./components/SuperConfig";
//import DarkWebScanner from "./components/DarkWebDomainScanner";

// AI Imports
import AIDashboard from "./components/AIDashboard";
import AIOrganizations from "./components/AIOrganizations";
import AIAssessment from "./components/AIAssessment";
import AIDeals from "./components/AIDeals";
import AIBusinessUnits from "./components/AIBusinessUnits";
import AIAssessmentRisksView from "./components/AIAssessmentRisksView";
import AiIntelligenceDashboard from "./components/AiIntelligenceDashboard";

// Twingly DarkWeb Domain Scanner
//import TwinglyDarkWebDomainScanner from "./components/TwinglyDarkWebDomainScanner";

// Enterprise Imports
import EnterpriseDashboard from "./components/EnterpriseDashboard";
import SocDashboard from "./components/SocDashboard";
import FusionPage from "./components/FusionPage";
import FusionRouteGuard from "./components/fusion/FusionRouteGuard";
import ModuleRouteGuard from "./components/ModuleRouteGuard";
import EnterpriseDeals from "./components/EnterpriseDeals";
import EnterpriseBusinessUnits from "./components/EnterpriseBusinessUnits";
import EnterpriseOrganizations from "./components/EnterpriseOrganizations";
import EnterpriseAssessment from "./components/EnterpriseAssessment";
import EnterpriseAssessmentRisksView from "./components/EnterpriseAssessmentRisksView";
import EnterpriseAssessmentComparisonView from "./components/EnterpriseAssessmentComparisonView";
import EnterpriseDocumentAssessment from "./components/EnterpriseDocumentAssessment";

// TPRM Imports
import TPRMDashboard from "./components/TPRMDashboard";
import TPRMOrganizations from "./components/TPRMOrganizations";
import TPRMAssessment from "./components/TPRMAssessment";
import TPRMDeals from "./components/TPRMDeals";
import TPRMBusinessUnits from "./components/TPRMBusinessUnits";
import TPRMApplications from "./components/TPRMApplications";
import TPRMAssessmentRisksView from "./components/TPRMAssessmentRisksView";
import TPRMDocumentAssessment from "./components/TPRMDocumentAssessment";
import VendorDashboard from "./components/VendorDashboard";
import TPRMVendors from "./components/TPRMVendors";
import CISOPortfolioView from "./components/CISOPortfolioView";

import AcceptInvite from "./components/UserAcceptInvite";
import InviteCallback from "./components/UserInviteCallback";
import Logout from "./components/Logout";
import Signup from "./components/Signup/Signup";
import MsspSignup from "./components/Signup/MsspSignup";
import SignupConfirmation from "./components/Signup/SignupConfirmation";
import TenantUserManagement from "./components/TenantUserManagement";
import DocumentAccessLogPage from "./components/DocumentAccessLogPage";
import MyTasks from "./components/MyTasks";
import Accounts from "./components/Accounts";
import FAQ from "./components/FAQ";
import UserGuide from "./components/UserGuide";
import ContactUs from "./components/ContactUs";
import SuperAdmin from "./components/SuperAdmin";
import ChatWidget from "./components/ChatWidget";
import SecurityInsightsDashboard from "./components/SecurityInsightsDashboard";
import BrandProtectionDashboard from "./components/BrandProtectionDashboard";
import WorkforceDashboard from "./components/WorkforceDashboard";
import AgentPage from "./components/AgentPage";

// Legal document pages
import TermsOfUse from "./components/Legal/TermsOfUse";
import PrivacyPolicy from "./components/Legal/PrivacyPolicy";
import DataProcessingAgreement from "./components/Legal/DataProcessingAgreement";
import VendorInviteeTerms from "./components/Legal/VendorInviteeTerms";


// Users with no breadth anywhere in the org have nothing to browse — SELF-scoped users, and
// deal/BU contributors/approvers, whose work reaches them through My Tasks. (Was
// isSelfScopeOnly() && !hasSelfScopeRole('RISK_MANAGER'); isAssignmentScopedOnly reproduces
// that for SELF-only users and also catches the deal/BU case.)
const SelfScopeGuard = ({ children }) => {
  const { isAssignmentScopedOnly } = useRBAC();
  if (isAssignmentScopedOnly()) {
    return <Navigate to="/my-tasks" replace />;
  }
  return children;
};

// Custom remediation workflows are behind Features:RemediationWorkflows (UI-only). The nav entry
// is hidden too; this covers direct URL access. Renders nothing until the flags have loaded so an
// opt-in flag is not mistaken for "off" on the first render.
const WorkflowsFeatureGuard = ({ children }) => {
  const enabled = useRemediationWorkflows();
  const loaded = useFeatureFlagsLoaded();
  if (!loaded) return null;
  if (!enabled) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
};

// Security Operations (/soc) is behind Features:Usoc. The nav entry is hidden too; this covers
// direct URL access. Renders nothing until the flags have loaded, like WorkflowsFeatureGuard.
const UsocFeatureGuard = ({ children }) => {
  const enabled = useUsoc();
  const loaded = useFeatureFlagsLoaded();
  if (!loaded) return null;
  if (!enabled) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
};

// Redirects SELF-scoped users away from Organization Assessment pages
const OrgAssessmentRoute = ({ children }) => {
  const { canViewOrganizationAssessments } = useRBAC();
  if (!canViewOrganizationAssessments()) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
};

// Allows ORG+ users (full portfolio) or deal/BU-scoped users (filtered portfolio)
const PortfolioRoute = ({ children }) => {
  const { canViewOrganizationAssessments, canViewDeal, canViewBusinessUnit } = useRBAC();
  const [searchParams] = useSearchParams();
  const dealId = searchParams.get("dealId");
  const businessUnitId = searchParams.get("businessUnitId");

  if (canViewOrganizationAssessments()) return children;
  const parsedDealId = dealId ? parseInt(dealId, 10) : NaN;
  if (!Number.isNaN(parsedDealId) && canViewDeal(parsedDealId)) return children;
  const parsedBUId = businessUnitId ? parseInt(businessUnitId, 10) : NaN;
  if (!Number.isNaN(parsedBUId) && canViewBusinessUnit(parsedBUId)) return children;

  return <Navigate to="/dashboard" replace />;
};

// Catch-all redirect: users with no breadth in the org go to /my-tasks, others to /dashboard
const DefaultRedirect = () => {
  const { isAssignmentScopedOnly } = useRBAC();
  const destination = isAssignmentScopedOnly() ? '/my-tasks' : '/dashboard';
  return <Navigate to={destination} replace />;
};

// Keyed by pathname so a render crash on one route shows a recovery UI, but navigating elsewhere
// remounts the boundary and recovers — instead of leaving the user stuck on a dead/blank tree.
function RouteErrorBoundary({ children }) {
  const location = useLocation();
  return <ErrorBoundary key={location.pathname}>{children}</ErrorBoundary>;
}

function App() {
  return (
    <Router>
      {localStorage.getItem('user') && <ChatWidget />}
      <RouteErrorBoundary>
      <Routes>
        {/* Public routes - no auth required */}
        <Route path="/" element={<Navigate to="/login" replace />} />
        <Route path="/login" element={<Login />} />
        <Route path="/logout" element={<Logout />} />
        <Route path="/invite/accept" element={<AcceptInvite />} />
        <Route path="/invite/callback" element={<InviteCallback />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/signup/mssp" element={<MsspSignup />} />
        <Route path="/signup/confirmation" element={<SignupConfirmation />} />

        {/* Legal document pages - public, no auth required */}
        <Route path="/legal/terms-of-use" element={<TermsOfUse />} />
        <Route path="/legal/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/legal/data-processing-agreement" element={<DataProcessingAgreement />} />
        <Route path="/legal/vendor-invitee-terms" element={<VendorInviteeTerms />} />

        {/* Dev-only branding gallery — stripped from production builds */}
        {process.env.NODE_ENV === 'development' && (
          <Route path="/brand-preview" element={<BrandPreview />} />
        )}

        {/* Protected routes - all require auth */}
        <Route element={<PrivateRoute />}>
          <Route path="/settings/integrations/consent-complete" element={<ConsentCallback />} />
          <Route path="/dashboard" element={<SelfScopeGuard><Dashboard /></SelfScopeGuard>} />
          <Route path="/organizations" element={<TenantSelection />} />
          <Route path="/my-tasks" element={<MyTasks />} />
          <Route path="/accounts" element={<Accounts />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/user-guide" element={<UserGuide />} />
          <Route path="/user-guide/:docId" element={<UserGuide />} />
          <Route path="/contact" element={<ContactUs />} />
          <Route path="/super-admin" element={<SuperAdmin />} />
          <Route path="/deals" element={<Deals />} />
          <Route path="/DealDashboard" element={<DealDashboard />} />
          <Route path="/businessunits" element={<BusinessUnits />} />
          <Route path="/risks" element={<AllRisks />} />
          <Route path="/attack-paths" element={<AttackPathsDetail />} />
          <Route path="/remediations" element={<Remediation />} />
          <Route path="/remediation-scorecard" element={
            <RemediationScorecardFeatureGuard>
              <RemediationScorecardPage />
            </RemediationScorecardFeatureGuard>
          } />
          <Route path="/riskRegister" element={<RiskRegister />} />
          <Route path="/users" element={
            <RoleProtectedRoute minimumRole="ADMIN" scope="ORG" allowDealBUAdmins>
              <UserManagement />
            </RoleProtectedRoute>
          } />
          <Route path="/settings/integrations" element={
            <RoleProtectedRoute minimumRole="RISK_MANAGER" scope="ORG" allowDealBUScoped>
              <IntegrationSettings />
            </RoleProtectedRoute>
          } />
          <Route path="/settings/workflows" element={
            <RoleProtectedRoute minimumRole="RISK_MANAGER" scope="ORG">
              <WorkflowsFeatureGuard>
                <WorkflowTemplatesPage />
              </WorkflowsFeatureGuard>
            </RoleProtectedRoute>
          } />
          <Route path="/settings/document-access" element={
            <RoleProtectedRoute minimumRole="RISK_MANAGER" scope="ORG">
              <DocumentAccessLogPage />
            </RoleProtectedRoute>
          } />
          <Route path="/tenant-users" element={<TenantUserManagement />} />
          <Route path="/subscription" element={<SubscriptionManagement />} />

          {/* Assessment routes */}
          <Route path="/assessment" element={<EnhancedAssessment />} />
          <Route path="/assessment/:id" element={<EnhancedAssessment />} />
          <Route path="/assessment/:id/risks" element={<AssessmentRisksView />} />

          {/* Business Unit Assessment routes */}
          <Route path="/BusinessUnitDashboard" element={<BusinessUnitDashboard />} />
          <Route path="/businessunitassessment" element={<BusinessUnitAssessment />} />
          <Route path="/businessunitassessment/:id" element={<BusinessUnitAssessment />} />
          <Route path="/businessunitassessment/:id/risks" element={<BusinessUnitAssessmentRisksView />} />

          {/* Enterprise routes */}
          <Route path="/EnterpriseDashboard" element={<EnterpriseDashboard />} />
          <Route path="/soc" element={
            <RoleProtectedRoute minimumRole="RISK_MANAGER" scope="ORG" allowSupportSuperAdminView>
              <UsocFeatureGuard>
                <SocDashboard />
              </UsocFeatureGuard>
            </RoleProtectedRoute>
          } />
          {/* Same gate as the nav tab: Features:Fusion, and not for assignment-scoped users. */}
          <Route path="/Fusion" element={<FusionRouteGuard><FusionPage /></FusionRouteGuard>} />
          <Route path="/EnterpriseDeals" element={<EnterpriseDeals />} />
          <Route path="/EnterpriseBusinessUnits" element={<EnterpriseBusinessUnits />} />
          <Route path="/EnterpriseOrganizations" element={<OrgAssessmentRoute><EnterpriseOrganizations /></OrgAssessmentRoute>} />
          <Route path="/EnterpriseAssessment" element={<EnterpriseAssessment />} />
          <Route path="/EnterpriseAssessment/:id" element={<EnterpriseAssessment />} />
          <Route path="/EnterpriseDocumentAssessment" element={<EnterpriseDocumentAssessment />} />
          <Route path="/EnterpriseAssessments/:id/risks" element={<EnterpriseAssessmentRisksView />} />
          <Route path="/EnterpriseAssessments/:id/compare/:previousId" element={<EnterpriseAssessmentComparisonView />} />

          {/* Ransomware routes */}
          <Route path="/RansomwareDashboard" element={<DashboardRansomware />} />
          <Route path="/RansomwareAssessment" element={<RansomwareAssessment />} />
          <Route path="/RansomwareAssessment/:id" element={<RansomwareAssessment />} />
          <Route path="/RansomwareDeals" element={<RansomwareDeals />} />
          <Route path="/RansomwareBusinessUnits" element={<RansomwareBusinessUnits />} />
          <Route path="/RansomwareOrganizations" element={<OrgAssessmentRoute><RansomwareOrganizations /></OrgAssessmentRoute>} />
          <Route path="/RansomwareAssessment/:id/risks" element={<RansomwareAssessmentRisksView />} />
          <Route path="/RansomwareAssessment/:id/executive" element={
            <RoleProtectedRoute minimumRole="VIEWER" scope="ORG">
              <RansomwareExecutiveView />
            </RoleProtectedRoute>
          } />

          {/* Dark Web routes */}
          <Route path="/DarkWebDashboard" element={<DarkWebDashboard />} />
          <Route path="/DarkWebAssessment" element={<DarkWebAssessment />} />
          <Route path="/DarkWebAssessment/:id" element={<DarkWebAssessment />} />
          <Route path="/DarkWebDeals" element={<DarkWebDeals />} />
          <Route path="/DarkWebBusinessUnits" element={<DarkWebBusinessUnits />} />
          <Route path="/DarkWebOrganizations" element={<OrgAssessmentRoute><DarkWebOrganizations /></OrgAssessmentRoute>} />
          <Route path="/DarkWebAssessment/:id/risks" element={<DarkWebAssessmentRisksView />} />
          <Route path="/DarkWebDomainConfig" element={<ModuleRouteGuard module="darkWebMonitoring"><DarkWebDomainConfig /></ModuleRouteGuard>} />
          <Route path="/DarkWebMonitoringDashboard" element={<ModuleRouteGuard module="darkWebMonitoring"><DarkWebLiveMonitoringDashboard /></ModuleRouteGuard>} />
          <Route path="/DarkWebSecretsScan" element={<ModuleRouteGuard module="secretsScanning"><SecretsScanningDashboard /></ModuleRouteGuard>} />
          {/* Legacy standalone secrets config retired — the page posted pre-target-model fields
              and could wipe scan targets on save. Redirect like /DarkWebEsmConfig below. */}
          <Route path="/DarkWebSecretsConfig" element={<Navigate to="/ConnectorsConfig?tab=secrets" replace />} />
          <Route path="/DarkWebEsmDashboard" element={<ModuleRouteGuard module="surfaceScan"><EsmDashboard /></ModuleRouteGuard>} />
          <Route path="/DarkWebEsmConfig" element={<Navigate to="/ConnectorsConfig" replace />} />
          <Route path="/TelegramSignals" element={<Navigate to="/DarkWebMonitoringDashboard" replace />} />
          <Route path="/TelegramConfig" element={<Navigate to="/SuperConfig" replace />} />
          <Route path="/SuperConfig" element={
            <RoleProtectedRoute superAdminOnly>
              <SuperConfig />
            </RoleProtectedRoute>
          } />
          <Route path="/FortinetConfig" element={<FortinetConfig />} />
          <Route path="/ConnectorsConfig" element={
            <RoleProtectedRoute minimumRole="RISK_MANAGER" scope="ORG" allowDealBUScoped>
              <ConnectorsConfig />
            </RoleProtectedRoute>
          } />

          {/* AI routes */}
          <Route path="/AIDashboard" element={<AIDashboard />} />
          <Route path="/AIAssessment" element={<AIAssessment />} />
          <Route path="/AIAssessment/:id" element={<AIAssessment />} />
          <Route path="/AIDeals" element={<AIDeals />} />
          <Route path="/AIBusinessUnits" element={<AIBusinessUnits />} />
          <Route path="/AIOrganizations" element={<OrgAssessmentRoute><AIOrganizations /></OrgAssessmentRoute>} />
          <Route path="/AIAssessment/:id/risks" element={<AIAssessmentRisksView />} />
          <Route path="/AIIntelligence" element={<ModuleRouteGuard module="aiIntelligence"><AiIntelligenceDashboard /></ModuleRouteGuard>} />

 		  {/*TPRM Routes */}
          <Route path="/TPRMDashboard" element={<TPRMDashboard />} />
          <Route path="/TPRMVendors" element={<OrgAssessmentRoute><TPRMVendors /></OrgAssessmentRoute>} />
          <Route path="/TPRMAssessment" element={<TPRMAssessment />} />
          <Route path="/TPRMAssessment/:id" element={<TPRMAssessment />} />
          <Route path="/TPRMDeals" element={<TPRMDeals />} />
          <Route path="/TPRMBusinessUnits" element={<TPRMBusinessUnits />} />
          <Route path="/TPRMApplications" element={<TPRMApplications />} />
          <Route path="/TPRMOrganizations" element={<OrgAssessmentRoute><TPRMOrganizations /></OrgAssessmentRoute>} />
          <Route path="/TPRMAssessment/:id/risks" element={<TPRMAssessmentRisksView />} />
          <Route path="/TPRMDocumentAssessment" element={<TPRMDocumentAssessment />} />
          <Route path="/VendorDashboard/:vendorId" element={<VendorDashboard />} />
          <Route path="/TPRMPortfolio" element={<PortfolioRoute><CISOPortfolioView /></PortfolioRoute>} />

          <Route path="/subscription" element={<PrivateRoute><SubscriptionManagement /></PrivateRoute>} />

          {/* Agent */}
          <Route path="/agent" element={<AgentPage />} />

          {/* Security Insights */}
          <Route path="/SecurityInsights" element={<SecurityInsightsDashboard />} />

          {/* Brand Protection */}
          <Route path="/BrandProtection" element={
            <RoleProtectedRoute minimumRole="RISK_MANAGER" scope="ORG" allowSupportSuperAdminView>
              <ModuleRouteGuard module="brandProtection"><BrandProtectionDashboard /></ModuleRouteGuard>
            </RoleProtectedRoute>
          } />

          {/* Workforce Intelligence Engine */}
          <Route path="/WorkforceIntelligence" element={
            <RoleProtectedRoute minimumRole="RISK_MANAGER" scope="ORG" allowSupportSuperAdminView>
              <WorkforceDashboard />
            </RoleProtectedRoute>
          } />
        </Route>

        {/* Catch-all route - redirect unknown paths based on scope */}
        <Route path="*" element={<DefaultRedirect />} />
      </Routes>
      </RouteErrorBoundary>
    </Router>
  );
}

export default App;
